import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ActiveTenancyDashboard } from '../components/tenant/ActiveTenancyDashboard';
import { PreTenancyDashboard } from '../components/tenant/PreTenancyDashboard';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SAVED_OPENRENT_STORAGE_KEY = 'saved_openrent_listings';
const INVITE_ACCEPTED_STORAGE_KEY = 'siyak_invite_accepted';

const getSavedOpenRentEntries = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = localStorage.getItem(SAVED_OPENRENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      return [];
    }

    const seen = new Set();
    return parsed
      .filter((entry) => entry && typeof entry === 'object' && typeof entry.external_url === 'string' && entry.external_url.trim())
      .filter((entry) => {
        const key = `${entry.id || ''}|${entry.external_url.trim()}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .map((entry) => ({
        id: `saved-openrent-${entry.id || entry.external_url}`,
        property_id: entry.id || entry.external_url,
        source: 'openrent',
        property: {
          ...entry,
          source: 'openrent',
        },
      }));
  } catch (error) {
    console.error('Failed to read saved OpenRent listings from storage:', error);
    return [];
  }
};

export const TenantDashboard = () => {
  useEffect(() => { document.title = 'Tenant Dashboard - SIYAK'; }, []);
  const { user, loading: authLoading, refreshUser } = useAuth();
  const dashboardErrorToastAtRef = useRef(0);
  const [rentSchedules, setRentSchedules] = useState([]);
  const [rentHistory, setRentHistory] = useState([]);
  const [issues, setIssues] = useState([]);
  const [transferRequests, setTransferRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [savedProperties, setSavedProperties] = useState([]);
  const [applications, setApplications] = useState([]);
  const [complianceDocuments, setComplianceDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingTransfer, setSubmittingTransfer] = useState(false);
  const [pendingApplicationRemoval, setPendingApplicationRemoval] = useState(null);
  const [removingApplication, setRemovingApplication] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);

  const mergeSavedProperties = useCallback((backendSaved = []) => {
    const openRentSaved = getSavedOpenRentEntries();
    return [...backendSaved, ...openRentSaved];
  }, []);

  const getApplicationDecisionStatus = useCallback(
    (application) => application?.decision_status || application?.status || 'pending',
    []
  );

  const isExpectedAuthError = useCallback((error) => {
    const status = error?.response?.status;
    return status === 401 || status === 403;
  }, []);

  const shouldShowDashboardErrorToast = useCallback(() => {
    const now = Date.now();
    if (now - dashboardErrorToastAtRef.current < 10000) {
      return false;
    }
    dashboardErrorToastAtRef.current = now;
    return true;
  }, []);

  const hasActiveTenancy = Boolean(user?.tenancy_id);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = sessionStorage.getItem(INVITE_ACCEPTED_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setInviteStatus(parsed && typeof parsed === 'object' ? parsed : null);
    } catch (error) {
      setInviteStatus(null);
    }
  }, [user?.id]);

  // Poll for new notifications every 10 seconds (works for all tenants, not just active ones)
  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/api/notifications`);
        setNotifications(response.data);
      } catch (error) {
        if (!isExpectedAuthError(error)) {
          console.error('Failed to poll notifications:', error);
        }
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [user?.id, isExpectedAuthError]);

  const fetchAllData = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const requests = [
        axios.get(`${API_URL}/api/saved-properties`),
        axios.get(`${API_URL}/api/applications`),
        axios.get(`${API_URL}/api/issues`),
        axios.get(`${API_URL}/api/notifications`)
      ];

      // Only fetch rent & transfer data if user has active tenancy
      if (hasActiveTenancy) {
        requests.push(
          axios.get(`${API_URL}/api/rent`),
          axios.get(`${API_URL}/api/rent-history`),
          axios.get(`${API_URL}/api/transfer-request/me`),
          axios.get(`${API_URL}/api/documents/tenant/my-property/compliance`)
        );
      }

      const results = await Promise.allSettled(requests);

      const [savedRes, appsRes, issuesRes, notifsRes, ...activeTenancyResults] = results;

      const backendSaved = savedRes.status === 'fulfilled' ? savedRes.value.data : [];
      setSavedProperties(mergeSavedProperties(backendSaved));
      setApplications(appsRes.status === 'fulfilled' ? appsRes.value.data : []);
      setIssues(issuesRes.status === 'fulfilled' ? issuesRes.value.data : []);
      setNotifications(notifsRes.status === 'fulfilled' ? notifsRes.value.data : []);

      const coreFailures = [savedRes, appsRes, issuesRes, notifsRes]
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason);

      if (hasActiveTenancy && activeTenancyResults.length >= 4) {
        const [rentRes, historyRes, transferRes, complianceRes] = activeTenancyResults;
        setRentSchedules(rentRes.status === 'fulfilled' ? rentRes.value.data : []);
        setRentHistory(historyRes.status === 'fulfilled' ? historyRes.value.data : []);
        setTransferRequests(transferRes.status === 'fulfilled' ? transferRes.value.data : []);
        
        setComplianceDocuments(
          complianceRes.status === 'fulfilled'
            ? (complianceRes.value.data?.documents || [])
            : []
        );

        if (complianceRes.status === 'rejected') {
          console.error('Failed to load compliance documents:', complianceRes.reason);
        }
      } else {
        // Prevent stale tenancy data when assignment/property context changes.
        setRentSchedules([]);
        setRentHistory([]);
        setTransferRequests([]);
        setComplianceDocuments([]);
      }

      const unexpectedCoreFailures = coreFailures.filter((error) => !isExpectedAuthError(error));
      if (unexpectedCoreFailures.length > 0 && shouldShowDashboardErrorToast()) {
        toast.error('Some data failed to load');
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      if (!isExpectedAuthError(error) && shouldShowDashboardErrorToast()) {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id, hasActiveTenancy, isExpectedAuthError, shouldShowDashboardErrorToast, mergeSavedProperties]);

  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }

    const onStorage = (event) => {
      if (event.key !== SAVED_OPENRENT_STORAGE_KEY) {
        return;
      }

      setSavedProperties((prev) => {
        const backendSaved = (prev || []).filter((entry) => entry?.source !== 'openrent');
        return mergeSavedProperties(backendSaved);
      });
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user?.id, mergeSavedProperties]);

  useEffect(() => {
    if (!user?.id) return;

    const run = async () => {
      await refreshUser();
      await fetchAllData();
    };

    run();
  }, [user?.id, user?.tenancy_id, refreshUser, fetchAllData]);

  const handleRequestTransfer = async () => {
    setSubmittingTransfer(true);
    try {
      const response = await axios.post(`${API_URL}/api/transfer-request`, {
        current_tenancy_id: null
      });
      setTransferRequests((prev) => [response.data, ...prev]);
      toast.success('Transfer request submitted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit transfer request');
    } finally {
      setSubmittingTransfer(false);
    }
  };

  const handleWithdrawTransferRequest = async (requestId) => {
    try {
      await axios.delete(`${API_URL}/api/transfer-request/${requestId}`);
      setTransferRequests((prev) => prev.filter((req) => req.id !== requestId));
      toast.success('Transfer request withdrawn');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw transfer request');
    }
  };

  const handleWithdrawApplication = async (application) => {
    if (!application?.id) return;

    setPendingApplicationRemoval(application);
  };

  const confirmWithdrawApplication = async () => {
    if (!pendingApplicationRemoval?.id) {
      setPendingApplicationRemoval(null);
      return;
    }

    try {
      setRemovingApplication(true);
      await axios.delete(`${API_URL}/api/applications/${pendingApplicationRemoval.id}`);
      setApplications((prev) => prev.filter((item) => item.id !== pendingApplicationRemoval.id));
      toast.success('Application removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove application');
    } finally {
      setRemovingApplication(false);
      setPendingApplicationRemoval(null);
    }
  };

  const handleViewReceipt = (receiptId) => {
    window.open(`${API_URL}/api/rent-history/${receiptId}/receipt`, '_blank');
  };

  const handleUnsaveSavedProperty = async (savedItem) => {
    if (!savedItem) {
      return;
    }

    const isOpenRent = savedItem.source === 'openrent' || savedItem.property?.source === 'openrent';

    if (isOpenRent) {
      const targetUrl = savedItem.property?.external_url || savedItem.external_url;
      try {
        const current = getSavedOpenRentEntries();
        const next = current.filter((entry) => entry.external_url !== targetUrl);
        localStorage.setItem(SAVED_OPENRENT_STORAGE_KEY, JSON.stringify(next));

        setSavedProperties((prev) =>
          (prev || []).filter((entry) => {
            if (entry.source !== 'openrent' && entry.property?.source !== 'openrent') {
              return true;
            }
            const entryUrl = entry.property?.external_url || entry.external_url;
            return entryUrl !== targetUrl;
          })
        );
        toast.success('Removed from saved properties');
      } catch (error) {
        console.error('Failed to unsave OpenRent listing:', error);
        toast.error('Failed to remove saved listing');
      }
      return;
    }

    const propertyId = savedItem.property_id;
    if (!propertyId) {
      toast.error('Saved property cannot be removed');
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/saved-properties/${propertyId}`);
      setSavedProperties((prev) => (prev || []).filter((entry) => entry.property_id !== propertyId));
      toast.success('Removed from saved properties');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove saved property');
    }
  };

  const handleMarkNotificationAsRead = async (notifId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notifId}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n))
      );
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleMarkSharedDocumentViewed = async (documentId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/document/${documentId}/viewed`);
      setNotifications((prev) =>
        prev.map((n) => (
          n.type === 'document_shared' && n.document_id === documentId
            ? { ...n, document_viewed: true }
            : n
        ))
      );
    } catch (error) {
      console.error('Failed to mark document notifications as viewed');
    }
  };

  const handleDeleteNotification = async (notifId) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${notifId}`);
      setNotifications((prev) => prev.filter((n) => n.id !== notifId));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const handleCreateIssue = async (issueData) => {
    const response = await axios.post(`${API_URL}/api/issues`, issueData);
    setIssues((prev) => [response.data, ...prev]);
    return response.data;
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {inviteStatus && (
        <div className="bg-green-50 border-b border-green-200">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium text-green-950">
                  You have successfully joined {inviteStatus.propertyTitle || user?.property_title || 'your property'}
                </p>
                <p className="text-sm text-green-800">
                  Your tenancy is active. You are now in your tenancy dashboard.
                </p>
              </div>
              <button
                type="button"
                className="self-start text-sm font-medium text-green-800 underline"
                onClick={() => {
                  setInviteStatus(null);
                  try {
                    sessionStorage.removeItem(INVITE_ACCEPTED_STORAGE_KEY);
                  } catch (error) {}
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      {hasActiveTenancy ? (
        <ActiveTenancyDashboard
          user={user}
          rentSchedules={rentSchedules}
          rentHistory={rentHistory}
          issues={issues}
          transferRequests={transferRequests}
          notifications={notifications}
          savedProperties={savedProperties}
          applications={applications}
          complianceDocuments={complianceDocuments}
          onUnsaveSavedProperty={handleUnsaveSavedProperty}
          onRequestTransfer={handleRequestTransfer}
          onWithdrawTransferRequest={handleWithdrawTransferRequest}
          onWithdrawApplication={handleWithdrawApplication}
          onMarkNotificationAsRead={handleMarkNotificationAsRead}
          onMarkSharedDocumentViewed={handleMarkSharedDocumentViewed}
          onDeleteNotification={handleDeleteNotification}
          isSubmitting={submittingTransfer}
          onViewReceipt={handleViewReceipt}
          onCreateIssue={handleCreateIssue}
        />
      ) : (
        <PreTenancyDashboard 
          user={user} 
          notifications={notifications}
          savedProperties={savedProperties}
          applications={applications}
          inviteStatus={inviteStatus}
          onUnsaveSavedProperty={handleUnsaveSavedProperty}
          onWithdrawApplication={handleWithdrawApplication}
          onMarkNotificationAsRead={handleMarkNotificationAsRead}
        />
      )}

      <AlertDialog
        open={Boolean(pendingApplicationRemoval)}
        onOpenChange={(open) => {
          if (!open) setPendingApplicationRemoval(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove application?</AlertDialogTitle>
            <AlertDialogDescription>
              {getApplicationDecisionStatus(pendingApplicationRemoval) === 'pending'
                ? 'Withdraw this application from your list?'
                : 'Remove this application from your list?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingApplication}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removingApplication}
              onClick={confirmWithdrawApplication}
            >
              {removingApplication ? 'Processing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};
